import { Box, Typography } from '@mui/material';
import React, {
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useUID } from 'react-uid';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  EmojiEmotionsOutlined,
} from '@mui/icons-material';

export interface RichTextEditorProps {
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
  /** Figma Input/Rich Text (8685:9298): B / I / U | lists | link. No emoji. */
  toolbar?: 'default' | 'mission';
}

interface ToolbarButtonProps {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}

const ToolbarButton = ({
  active,
  onClick,
  children,
  disabled,
}: ToolbarButtonProps) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      border: 'none',
      background: active ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
      borderRadius: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      color: disabled ? '#999' : '#383838',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.15s ease',
      '&:hover': {
        background: disabled ? 'transparent' : 'rgba(0, 0, 0, 0.08)',
      },
      '& svg': {
        fontSize: 20,
      },
    }}
  >
    {children}
  </Box>
);

export const RichTextEditor = forwardRef(
  (
    {
      name,
      label,
      placeholder = 'Describe your event',
      value = '',
      onChange,
      onBlur,
      hasError = false,
      minHeight = 120,
      maxHeight = 400,
      disabled = false,
      toolbar = 'default',
    }: RichTextEditorProps,
    ref?: React.Ref<HTMLDivElement>,
  ): ReactElement => {
    const id = name || `rich-text-${useUID()}`;
    const editorRef = useRef<HTMLDivElement>(null);
    const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
    const [isEmpty, setIsEmpty] = useState(true);

    useImperativeHandle(ref, () => editorRef.current as HTMLDivElement);

    const updateActiveFormats = useCallback(() => {
      const formats = new Set<string>();
      if (document.queryCommandState('bold')) formats.add('bold');
      if (document.queryCommandState('italic')) formats.add('italic');
      if (document.queryCommandState('underline')) formats.add('underline');
      if (document.queryCommandState('insertUnorderedList'))
        formats.add('bulletList');
      if (document.queryCommandState('insertOrderedList'))
        formats.add('numberedList');
      setActiveFormats(formats);
    }, []);

    const checkEmpty = useCallback(() => {
      if (editorRef.current) {
        const text = editorRef.current.textContent || '';
        setIsEmpty(text.trim().length === 0);
      }
    }, []);

    const handleContentChange = useCallback(() => {
      if (editorRef.current && onChange) {
        const html = editorRef.current.innerHTML;
        const text = editorRef.current.textContent || '';
        onChange(text.trim() === '' ? '' : html);
      }
      checkEmpty();
    }, [onChange, checkEmpty]);

    const execCommand = useCallback(
      (command: string, commandValue?: string) => {
        if (disabled) return;
        editorRef.current?.focus();
        document.execCommand(command, false, commandValue);
        updateActiveFormats();
        handleContentChange();
      },
      [disabled, updateActiveFormats, handleContentChange],
    );

    const handleBold = () => execCommand('bold');
    const handleItalic = () => execCommand('italic');
    const handleUnderline = () => execCommand('underline');
    const handleBulletList = () => execCommand('insertUnorderedList');
    const handleNumberedList = () => execCommand('insertOrderedList');

    const handleLink = () => {
      if (disabled) return;
      editorRef.current?.focus();
      const selection = window.getSelection()?.toString().trim() ?? '';
      const url = selection.startsWith('http') ? selection : `https://${selection}`;
      if (!selection) return;
      execCommand('createLink', url);
    };

    const handleEmoji = () => {
      if (disabled) return;
      editorRef.current?.focus();
      document.execCommand('insertText', false, '😊');
      handleContentChange();
    };

    const isMissionToolbar = toolbar === 'mission';

    useEffect(() => {
      if (editorRef.current && value !== undefined) {
        if (editorRef.current.innerHTML !== value) {
          editorRef.current.innerHTML = value;
          checkEmpty();
        }
      }
    }, [value, checkEmpty]);

    useEffect(() => {
      const handleSelectionChange = () => {
        if (editorRef.current && document.activeElement === editorRef.current)
          updateActiveFormats();
      };

      document.addEventListener('selectionchange', handleSelectionChange);
      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
      };
    }, [updateActiveFormats]);

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        if (disabled) return;
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
        handleContentChange();
      },
      [disabled, handleContentChange],
    );

    return (
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          '& .label': {
            fontSize: 14,
            fontWeight: 600,
            color: theme =>
              hasError ? theme.palette.error.main : theme.palette.text.primary,
            cursor: 'pointer',
            marginBottom: '4px',
          },
        }}
      >
        {label && (
          <label htmlFor={id} className="label">
            {label}
          </label>
        )}

        <Box
          sx={{
            border: '1px solid',
            borderColor: theme =>
              hasError ? theme.palette.error.light : theme.palette.neutral.dark,
            borderRadius: '1px',
            overflow: 'hidden',
            backgroundColor: theme => theme.palette.neutral.main,
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:focus-within': {
              borderColor: theme => theme.palette.primary.main,
              boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              borderBottom: '1px solid',
              borderColor: theme => theme.palette.neutral.dark,
            }}
          >
            <ToolbarButton
              active={activeFormats.has('bold')}
              onClick={handleBold}
              disabled={disabled}
            >
              <FormatBold />
            </ToolbarButton>
            <ToolbarButton
              active={activeFormats.has('italic')}
              onClick={handleItalic}
              disabled={disabled}
            >
              <FormatItalic />
            </ToolbarButton>
            <ToolbarButton
              active={activeFormats.has('underline')}
              onClick={handleUnderline}
              disabled={disabled}
            >
              <FormatUnderlined />
            </ToolbarButton>
            {isMissionToolbar ? (
              <Box
                sx={{
                  width: '1px',
                  height: 18,
                  backgroundColor: '#E4E6EB',
                  flexShrink: 0,
                }}
              />
            ) : null}
            <ToolbarButton
              active={activeFormats.has('bulletList')}
              onClick={handleBulletList}
              disabled={disabled}
            >
              <FormatListBulleted />
            </ToolbarButton>
            {isMissionToolbar ? (
              <ToolbarButton
                active={activeFormats.has('numberedList')}
                onClick={handleNumberedList}
                disabled={disabled}
              >
                <FormatListNumbered />
              </ToolbarButton>
            ) : null}
            {isMissionToolbar ? (
              <Box
                sx={{
                  width: '1px',
                  height: 18,
                  backgroundColor: '#E4E6EB',
                  flexShrink: 0,
                }}
              />
            ) : null}
            {isMissionToolbar ? (
              <ToolbarButton onClick={handleLink} disabled={disabled}>
                <LinkIcon />
              </ToolbarButton>
            ) : (
              <ToolbarButton onClick={handleEmoji} disabled={disabled}>
                <EmojiEmotionsOutlined />
              </ToolbarButton>
            )}
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Box
              ref={editorRef}
              id={id}
              contentEditable={!disabled}
              suppressContentEditableWarning
              onInput={handleContentChange}
              onBlur={() => {
                handleContentChange();
                onBlur?.();
              }}
              onPaste={handlePaste}
              sx={{
                minHeight: minHeight,
                maxHeight: maxHeight,
                overflowY: 'auto',
                padding: '12px 16px',
                outline: 'none',
                fontFamily: 'Poppins, sans-serif',
                fontSize: 14,
                lineHeight: 1.6,
                color: '#383838',
                cursor: disabled ? 'not-allowed' : 'text',
                opacity: disabled ? 0.6 : 1,
                '& ul, & ol': {
                  marginLeft: '20px',
                  paddingLeft: '0',
                },
                '& li': {
                  marginBottom: '4px',
                },
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '3px',
                },
              }}
            />

            {isEmpty && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: '12px',
                  left: '16px',
                  color: '#9e9e9e',
                  fontSize: 14,
                  fontFamily: 'Poppins, sans-serif',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {placeholder}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);
